import { Badge, Command, CommandItem, CommandList, Popover, PopoverContent, PopoverTrigger } from '@ibsys/kit';
import { CheckIcon } from 'lucide-react';
import { FC, useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getVerificationStatusLabel,
  useUpdateExpertReportVerificationStatus,
  VERIFICATION_STATUSES,
  type VerificationStatus,
} from '@/entities/expert-report';
import { cn } from '@/shared/lib';
import { Tooltip } from '@/shared/ui';
import { ExpertReportResultEditContext } from '../model';
import { ExpertReportVerificationStatusHistory } from './ExpertReportVerificationStatusHistory';

type Props = {
  conversationId: number;
  expertReportId: number;
  /** Стадия, на которой стоит проверка результата */
  status: VerificationStatus;
};

/** Вид метки по стадии проверки: чем дальше проверка ушла, тем заметнее метка */
const STATUS_BADGE: Record<VerificationStatus, { variant: 'primary' | 'secondary'; outline: boolean }> = {
  NEW: { variant: 'secondary', outline: true },
  IN_PROGRESS: { variant: 'primary', outline: true },
  CONFIRMED: { variant: 'primary', outline: false },
};

/**
 * Стадия проверки результата заключения человеком и её история.
 *
 * Стадию видит каждый, кто видит диалог; меняет — только тот, кому разрешена правка результата:
 * ему метка открывает выбор стадии, остальным остаётся меткой. Проверка идёт своим ходом, отдельно
 * от расчёта результата, поэтому и показана она отдельно от статуса расчёта.
 */
export const ExpertReportVerificationStatus: FC<Props> = ({ conversationId, expertReportId, status }) => {
  const { t } = useTranslation(['dialogs']);
  const canEdit = useContext(ExpertReportResultEditContext);
  const [isOpen, setIsOpen] = useState(false);
  const { mutate, isPending } = useUpdateExpertReportVerificationStatus();
  const badgeStyle = STATUS_BADGE[status];
  const label = getVerificationStatusLabel(t, status);

  const handleSelect = useCallback(
    (selected: VerificationStatus) => {
      setIsOpen(false);
      // Действующая стадия ничего не меняет и в историю не идёт — незачем и спрашивать сервер
      if (selected === status) return;
      // Ошибку показывает общий перехватчик запросов, а действующую стадию приносит ответ сервера
      mutate({ conversationId, expertReportId, verificationStatus: selected });
    },
    [conversationId, expertReportId, status, mutate],
  );

  const statusControl = canEdit ? (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      {/* Подсказка обнимает метку вместе с триггером: вставленная между ними, она потеряет его
          свойства — и выбор стадии перестанет открываться */}
      <Tooltip tooltip={t('dialogs:expertReport.changeVerificationStatus')}>
        <PopoverTrigger asChild>
          <Badge
            role="button"
            tabIndex={-1}
            variant={badgeStyle.variant}
            outline={badgeStyle.outline}
            className="font-normal cursor-pointer"
          >
            {label}
          </Badge>
        </PopoverTrigger>
      </Tooltip>
      <PopoverContent className="w-auto p-1" align="start">
        <Command shouldFilter={false}>
          <CommandList>
            {VERIFICATION_STATUSES.map((option) => (
              <CommandItem key={option} value={option} disabled={isPending} onSelect={() => handleSelect(option)}>
                <CheckIcon className={cn('size-4', option === status ? 'opacity-100' : 'opacity-0')} />
                {getVerificationStatusLabel(t, option)}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  ) : (
    <Tooltip tooltip={t('dialogs:expertReport.verificationStatusTitle')}>
      <Badge variant={badgeStyle.variant} outline={badgeStyle.outline} className="font-normal">
        {label}
      </Badge>
    </Tooltip>
  );

  return (
    <span className="flex shrink-0 items-center gap-1">
      {statusControl}
      <ExpertReportVerificationStatusHistory conversationId={conversationId} expertReportId={expertReportId} />
    </span>
  );
};
